/**
 * Google Drive Service for Testing
 * Simulates Google Drive functionality without requiring real API credentials
 */

import type { Character } from '../types/character';
import {
  SaveResult,
  LoadResult,
  CharacterSummary,
  GoogleDriveAuth,
  ConflictResolution,
  ConflictResolutionType,
  StorageLocation
} from '../types/persistence';
import { calculateChecksum } from '../utils/checksum';

// Temporary mock validation function
const validate = (_character: unknown) => ({ success: true, errors: [] });

interface DriveFile {
  id: string;
  name: string;
  content: string;
  modifiedTime: Date;
  characterId: string;
}

interface StoredDriveFile {
  id: string;
  name: string;
  content: string;
  modifiedTime: string; // stored as string, converted to Date
  characterId: string;
}

export class MockGoogleDriveService {
  private signedIn = false;
  private mockFiles = new Map<string, DriveFile>();
  private simulateNetworkDelay = true;
  private simulateErrors = false;

  constructor() {
    // Load mock data from localStorage to persist across sessions
    this.loadData();
  }

  /**
   * Simulate network delay for realistic testing
   */
  private async delay(ms: number = 500): Promise<void> {
    if (this.simulateNetworkDelay) {
      await new Promise((resolve) => setTimeout(resolve, ms));
    }
  }

  /**
   * Load mock data from localStorage
   */
  private loadData(): void {
    try {
      const mockData = localStorage.getItem('mock-google-drive-files');
      if (mockData) {
        const files = JSON.parse(mockData);
        this.mockFiles = new Map(
          files.map((file: StoredDriveFile) => [
            file.id,
            { ...file, modifiedTime: new Date(file.modifiedTime) }
          ])
        );
      }
    } catch (error) {
      console.warn('Failed to load mock Google Drive data:', error);
    }
  } /*
   *
   * Save mock data to localStorage
   */
  private saveData(): void {
    try {
      const files = Array.from(this.mockFiles.values()).map((file) => ({
        ...file,
        modifiedTime: file.modifiedTime.toISOString()
      }));
      localStorage.setItem('mock-google-drive-files', JSON.stringify(files));
    } catch (error) {
      console.warn('Failed to save mock Google Drive data:', error);
    }
  }

  /**
   * Sign in to mock Google Drive
   */
  async signIn(): Promise<boolean> {
    await this.delay(1000);

    if (this.simulateErrors && Math.random() < 0.1) {
      throw new Error('sign-in failed');
    }

    this.signedIn = true;
    console.log('Google Drive: Signed in successfully');
    return true;
  }

  /**
   * Sign out from mock Google Drive
   */
  async signOut(): Promise<void> {
    await this.delay(500);
    this.signedIn = false;
    console.log('Google Drive: Signed out');
  }

  /**
   * Check if signed in
   */
  isSignedIn(): boolean {
    return this.signedIn;
  }

  /**
   * Get authentication state
   */
  getAuthState(): GoogleDriveAuth {
    return {
      isSignedIn: this.signedIn,
      accessToken: this.signedIn ? 'mock-access-token' : undefined,
      expiresAt: this.signedIn ? new Date(Date.now() + 3600000) : undefined
    };
  }

  /**
   * Check if authenticated (async version for compatibility)
   */
  async isAuthenticated(): Promise<boolean> {
    return this.signedIn;
  }

  /**
 
  * Save character to mock Google Drive
   */
  async save(character: Character): Promise<SaveResult> {
    await this.delay();

    try {
      if (!this.signedIn) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Not signed in to Google Drive'
        };
      }

      if (this.simulateErrors && Math.random() < 0.05) {
        throw new Error('network error during save');
      }

      // Validate character
      const validation = validate(character);
      if (!validation.success) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: `validation failed: ${validation.errors.join(', ')}`
        };
      }

      // Create mock file
      const fileId = `mock-file-${character.id}`;
      const mockFile: DriveFile = {
        id: fileId,
        name: `character-${character.id}.json`,
        content: JSON.stringify({
          character,
          metadata: {
            version: 1,
            timestamp: new Date().toISOString(),
            checksum: calculateChecksum(JSON.stringify(character)),
            schemaVersion: character.schemaVersion
          }
        }),
        modifiedTime: new Date(),
        characterId: character.id
      };

      this.mockFiles.set(fileId, mockFile);
      this.saveData();

      console.log(`Google Drive: Saved character ${character.name} (${character.id})`);

      return {
        success: true,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Google Drive save error:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Load character from mock Google Drive
   */
  async load(characterId: string): Promise<LoadResult> {
    await this.delay();

    try {
      if (!this.signedIn) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Not signed in to Google Drive'
        };
      }

      if (this.simulateErrors && Math.random() < 0.05) {
        throw new Error('network error during load');
      }

      // Find character file
      const mockFile = Array.from(this.mockFiles.values()).find(
        (file) => file.characterId === characterId
      );

      if (!mockFile) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Character not found in Google Drive'
        };
      }

      // Parse file content
      const fileContent = JSON.parse(mockFile.content);
      const character = fileContent.character;

      console.log(`Google Drive: Loaded character ${character.name} (${character.id})`);

      return {
        success: true,
        character,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Google Drive load error:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete character from mock Google Drive
   */
  async delete(characterId: string): Promise<SaveResult> {
    await this.delay();

    try {
      if (!this.signedIn) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Not signed in to Google Drive'
        };
      }

      if (this.simulateErrors && Math.random() < 0.05) {
        throw new Error('network error during delete');
      }

      // Find and delete character file
      const fileToDelete = Array.from(this.mockFiles.entries()).find(
        ([_, file]) => file.characterId === characterId
      );

      if (!fileToDelete) {
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Character not found in Google Drive'
        };
      }

      this.mockFiles.delete(fileToDelete[0]);
      this.saveData();

      console.log(`Google Drive: Deleted character ${characterId}`);

      return {
        success: true,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date()
      };
    } catch (error) {
      console.error('Google Drive delete error:', error);
      return {
        success: false,
        location: StorageLocation.GoogleDrive,
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  } /**
 
  * List characters in mock Google Drive
   */
  async list(): Promise<CharacterSummary[]> {
    await this.delay();

    try {
      if (!this.signedIn) {
        return [];
      }

      if (this.simulateErrors && Math.random() < 0.05) {
        throw new Error('network error during list');
      }

      const summaries: CharacterSummary[] = [];

      for (const mockFile of this.mockFiles.values()) {
        try {
          const fileContent = JSON.parse(mockFile.content);
          const character = fileContent.character;

          summaries.push({
            id: character.id,
            name: character.name,
            career: character.career.name || 'Unknown',
            level: Math.floor(character.experience.spent / 100) + 1,
            lastModified: mockFile.modifiedTime,
            location: StorageLocation.GoogleDrive,
            syncStatus: {
              synced: true,
              pending: false,
              conflict: false,
              error: false,
              offline: false
            }
          });
        } catch (error) {
          console.warn(`Failed to parse mock file ${mockFile.name}:`, error);
        }
      }

      console.log(`Google Drive: Listed ${summaries.length} characters`);
      return summaries;
    } catch (error) {
      console.error('Google Drive list error:', error);
      return [];
    }
  }

  /**
   * Check for conflicts (mock implementation)
   */
  async checkForConflicts(
    local: Character
  ): Promise<{ hasConflict: boolean; resolution?: ConflictResolution }> {
    await this.delay(200);

    // Simulate conflict detection
    const hasConflict = Math.random() < 0.1; // 10% chance of conflict for testing

    if (hasConflict) {
      const remote = { ...local, name: `${local.name} (Remote)` };

      return {
        hasConflict: true,
        resolution: {
          characterId: local.id,
          localVersion: local,
          remoteVersion: remote,
          resolution: ConflictResolutionType.CreateCopy,
          timestamp: new Date()
        }
      };
    }

    return { hasConflict: false };
  }
  /**
   * Resolve conflict (mock implementation)
   */
  async resolveConflict(resolution: ConflictResolution): Promise<SaveResult> {
    await this.delay();

    console.log(`Google Drive: Resolng conflict with strategy ${resolution.resolution}`);

    switch (resolution.resolution) {
      case ConflictResolutionType.UseLocal:
        return await this.save(resolution.localVersion);

      case ConflictResolutionType.UseRemote:
        return {
          success: true,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date()
        };

      case ConflictResolutionType.CreateCopy: {
        const copy = {
          ...resolution.localVersion,
          id: `${resolution.localVersion.id}-copy-${Date.now()}`,
          name: `${resolution.localVersion.name} (Copy)`
        };
        return await this.save(copy);
      }

      default:
        return {
          success: false,
          location: StorageLocation.GoogleDrive,
          timestamp: new Date(),
          error: 'Unknown conflict resolution type'
        };
    }
  }

  /**
   * Configure mock behaor for testing
   */
  setConfig(config: { simulateNetworkDelay?: boolean; simulateErrors?: boolean }): void {
    if (config.simulateNetworkDelay !== undefined) {
      this.simulateNetworkDelay = config.simulateNetworkDelay;
    }
    if (config.simulateErrors !== undefined) {
      this.simulateErrors = config.simulateErrors;
    }
    console.log('Google Drive config updated:', config);
  }

  /**
   * Clear all mock data
   */
  clearData(): void {
    this.mockFiles.clear();
    this.saveData();
    console.log('Google Drive: All data cleared');
  }
  /**
   * Add sample data for testing
   */
  addSampleData(): void {
    const sample = {
      id: 'sample-character-1',
      name: 'Sample Rogue Trader',
      schemaVersion: 1,
      career: {
        id: 'rogue-trader',
        name: 'Rogue Trader',
        description: 'A powerful merchant-explorer',
        startingSkills: [],
        startingTalents: [],
        startingEquipment: [],
        aptitudes: []
      },
      homeworld: {
        id: 'imperial-world',
        name: 'Imperial World',
        description: 'A cilized Imperial world',
        characteristicModifiers: {},
        startingSkills: [],
        startingTalents: [],
        startingEquipment: [],
        aptitudes: []
      },
      birthright: { id: 'ship-captain', name: 'Ship Captain' },
      lure: { id: 'fortune', name: 'Fortune' },
      trials: { id: 'press-ganged', name: 'Press-ganged' },
      motivation: { id: 'prestige', name: 'Prestige' },
      characteristics: {
        weaponSkill: 35,
        ballisticSkill: 40,
        strength: 30,
        toughness: 35,
        agility: 35,
        intelligence: 45,
        perception: 40,
        willpower: 40,
        fellowship: 50,
        influence: 45
      },
      wounds: 14,
      fatigue: 0,
      corruption: 0,
      insanity: 0,
      profitFactor: 35,
      skills: [],
      talents: [],
      traits: [],
      equipment: [],
      weapons: [],
      armor: [],
      experience: {
        total: 8000,
        spent: 6000,
        available: 2000
      },
      enabledRulebooks: ['core'],
      manualAdjustments: [],
      createdAt: new Date('2024-01-01'),
      lastModified: new Date(),
      derivedStats: {
        wounds: 14,
        fatigue: 0,
        corruption: 0,
        insanity: 0
      }
    } as unknown as Character;

    this.mockFiles.set('sample-file-1', {
      id: 'sample-file-1',
      name: 'character-sample-character-1.json',
      content: JSON.stringify({
        character: sample,
        metadata: {
          version: 1,
          timestamp: new Date().toISOString(),
          checksum: calculateChecksum(JSON.stringify(sample)),
          schemaVersion: 1
        }
      }),
      modifiedTime: new Date(),
      characterId: sample.id
    });

    this.saveData();
    console.log('Google Drive: Sample data added');
  }
}

// Export singleton instance
export const mockGoogleDriveService = new MockGoogleDriveService();
